import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import type { NewTierPlansGridProps } from './types';
import * as S from './styles';

export function NewTierPlansGrid({
  notice,
  plans,
  highlightedPlanKey,
  density = 'compact',
  personalizedCard,
}: NewTierPlansGridProps) {
  return (
    <S.Container>
      {notice && (
        <S.Notice>
          <InfoOutlinedIcon data-testid="NewTierPlansGridNoticeIcon" />
          <div>
            <S.NoticeTitle>{notice.title}</S.NoticeTitle>
            <S.NoticeDescription>{notice.description}</S.NoticeDescription>
          </div>
        </S.Notice>
      )}

      <S.Grid data-testid="NewTierPlansGridGrid" $density={density} $columns={plans.length}>
        {plans.map((plan) => {
          const isHighlighted = plan.key === highlightedPlanKey && !plan.isCurrent;
          const isDisabledCta = plan.isCurrent || plan.isDowngrade;

          return (
            <S.PlanCard
              key={plan.key}
              data-testid={`NewTierPlansGridCard-${plan.key}`}
              $density={density}
              $highlighted={isHighlighted}
              $inverse={plan.key === 'black'}
            >
              {isHighlighted && <S.Badge>Popular</S.Badge>}
              {plan.isCurrent && <S.CurrentPlanBadge>Plano atual</S.CurrentPlanBadge>}

              {density === 'comfortable' ? (
                <S.NameBlock>
                  <S.PlanTitle>{plan.title}</S.PlanTitle>
                  <S.PlanTagline>{plan.tagline}</S.PlanTagline>
                </S.NameBlock>
              ) : (
                <S.PlanTitle>{plan.title}</S.PlanTitle>
              )}

              <S.PlanPrice $density={density}>
                {plan.price}
                <S.PlanPricePeriod $density={density}>/mês</S.PlanPricePeriod>
              </S.PlanPrice>

              {density !== 'comfortable' && <S.PlanTagline>{plan.tagline}</S.PlanTagline>}

              {plan.summaryFeatures && (
                <S.FeatureList>
                  {plan.summaryFeatures.map((feature) => (
                    <S.FeatureRow
                      key={feature.code}
                      $enabled={feature.enabled}
                      $inverse={plan.key === 'black'}
                    >
                      {feature.enabled ? (
                        <CheckIcon aria-hidden fontSize="small" />
                      ) : (
                        <CloseIcon aria-hidden fontSize="small" />
                      )}
                      <span>{feature.label}</span>
                    </S.FeatureRow>
                  ))}
                </S.FeatureList>
              )}

              {plan.cta && (
                <S.PlanCta
                  type="button"
                  $inverse={plan.key === 'black'}
                  disabled={isDisabledCta}
                  onClick={isDisabledCta ? undefined : plan.cta.onClick}
                >
                  {plan.isCurrent ? 'Seu plano atual' : plan.cta.label}
                </S.PlanCta>
              )}
            </S.PlanCard>
          );
        })}
      </S.Grid>

      {personalizedCard && (
        <S.PersonalizedBanner data-testid="NewTierPlansGridPersonalizedBanner">
          <S.PersonalizedContent>
            <S.NameBlock>
              <S.PersonalizedTitle>{personalizedCard.title}</S.PersonalizedTitle>
              <S.PersonalizedSubtitle>{personalizedCard.subtitle}</S.PersonalizedSubtitle>
            </S.NameBlock>

            <S.PersonalizedItems data-testid="NewTierPlansGridPersonalizedItems">
              {personalizedCard.items.map((item) => (
                <S.PersonalizedItem key={item}>
                  <CheckIcon aria-hidden fontSize="small" />
                  <span>{item}</span>
                </S.PersonalizedItem>
              ))}
            </S.PersonalizedItems>
          </S.PersonalizedContent>

          <S.PersonalizedAction>
            <S.PersonalizedCta type="button" onClick={personalizedCard.cta.onClick}>
              <WhatsAppIcon aria-hidden />
              {personalizedCard.cta.label}
            </S.PersonalizedCta>
            <S.PersonalizedHint>{personalizedCard.hint}</S.PersonalizedHint>
          </S.PersonalizedAction>
        </S.PersonalizedBanner>
      )}
    </S.Container>
  );
}

export default NewTierPlansGrid;
